import React, { useEffect, useState } from "react";
import { FIRM } from "../../data/assessment";
import { Printer, Mail, RotateCcw, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";

function Gauge({ score, color, label, tier }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(score); return; }
    let raf; const start = performance.now(); const dur = 1200;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setVal(Math.round(score * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);
  const r = 84, c = 2 * Math.PI * r, dash = (val / 100) * c;
  return (
    <div className="relative w-[220px] h-[220px] shrink-0">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={r} fill="none" stroke="#E6EEF4" strokeWidth="16" />
        <circle cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth="16" strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`} style={{ transition: "stroke-dasharray 0.1s linear" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="font-sora text-5xl font-extrabold" style={{ color }}>{val}</div>
          <div className="font-mono-plex text-[11px] text-[color:var(--tcc-ink-soft)] mt-1">out of 100</div>
        </div>
      </div>
    </div>
  );
}

function Bar({ area }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setW(area.score); return; }
    const t = setTimeout(() => setW(area.score), 120);
    return () => clearTimeout(t);
  }, [area.score]);
  return (
    <div className="tcc-no-break py-3 border-b border-[color:var(--tcc-line)] last:border-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="font-sora font-semibold text-[color:var(--tcc-ink)] text-[15px]">{area.name}</div>
          {area.nist && <div className="font-mono-plex text-[10px] uppercase tracking-wider text-[color:var(--tcc-ink-soft)]">{area.nist}</div>}
        </div>
        <div className="text-right shrink-0">
          <span className="font-mono-plex font-semibold" style={{ color: area.tier.color }}>{area.score}/100</span>
          <span className="ml-2 text-xs font-semibold" style={{ color: area.tier.color }}>{area.tier.label}</span>
        </div>
      </div>
      <div className="relative mt-2 h-3 rounded-full bg-[color:var(--tcc-surface-2)] overflow-visible">
        <div className="h-full rounded-full" style={{ width: `${w}%`, background: area.tier.color, transition: "width 1s ease" }} />
        {/* peer marker */}
        <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${area.peer}%` }} title={`Peer: ${area.peer}`}>
          <div className="w-[2px] h-5 bg-[color:var(--tcc-navy)]" />
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-[color:var(--tcc-navy)]" />
        </div>
      </div>
    </div>
  );
}

const SEV_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

export default function Report({ report, name, company, email, mobile, onRestart }) {
  const { profile, tier, overall, areaList, scope, risks, blindSpots, verifyLines, roadmap, strongest, weakest, criticalCount, highCount, peerAvg, verdict } = report;
  const sector = profile.A1;
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  // Executive narrative
  const narrative = buildNarrative(report, sector);

  const mailtoBody = encodeURIComponent(
    `Name: ${name}\nCompany: ${company}\nEmail: ${email}\nMobile: ${mobile || ""}\nSector: ${sector}\nSize: ${profile.A2}\nOverall score: ${overall}/100 (${tier.label} · ${tier.tier})\n\nArea scores:\n${areaList.map((a) => `- ${a.name}: ${a.score}/100`).join("\n")}\n\nTop risks:\n${risks.slice(0, 3).map((r) => `- [${r.level}] ${r.control} (${r.area})`).join("\n")}`
  );
  const mailto = `mailto:${FIRM.email}?subject=${encodeURIComponent(`Cyber Security Check — ${company}`)}&body=${mailtoBody}`;

  return (
    <div className="tcc-report tcc-fade max-w-4xl mx-auto">
      {/* 6.1 Header */}
      <div className="tcc-no-break rounded-t-2xl p-7 sm:p-9 text-white" style={{ background: "linear-gradient(135deg,#153A68,#0C2136)" }}>
        <div className="flex items-center gap-3">
          <img src={FIRM.logo} alt="Techmate" className="h-14 w-14 object-contain" />
          <div className="font-sora font-extrabold text-lg tracking-tight">TECHMATE <span className="font-normal text-white/70 text-sm">Infosec Solutions</span></div>
        </div>
        <h1 className="mt-6 font-sora text-3xl sm:text-4xl font-extrabold tracking-tight">Cyber Security &amp; Compliance Report</h1>
        <div className="mt-2 font-mono-plex text-[12px] sm:text-[13px] text-white/70">
          Prepared for {name} · {company} · {sector} · {today}
        </div>
        {email && <div className="font-mono-plex text-[11px] text-white/50 mt-0.5">{email}{mobile ? ` · ${mobile}` : ""}</div>}
      </div>

      <div className="rounded-b-2xl border border-t-0 border-[color:var(--tcc-line)] bg-white p-6 sm:p-9 space-y-10">
        {/* 6.2 Executive summary */}
        <section className="tcc-no-break">
          <SectionTitle>Executive summary</SectionTitle>
          <div className="mt-4 flex flex-col sm:flex-row items-center gap-6">
            <div className="text-center">
              <Gauge score={overall} color={tier.color} />
              <div className="mt-2 inline-flex items-center rounded-full px-3 py-1 font-mono-plex text-[11px] font-semibold uppercase tracking-wider text-white" style={{ background: tier.color }}>
                {tier.label} · {tier.tier} maturity
              </div>
            </div>
            <p className="text-[15px] leading-relaxed text-[color:var(--tcc-ink-soft)] font-inter">{narrative}</p>
          </div>
        </section>

        {/* 6.3 Compliance scope */}
        <section className="tcc-no-break">
          <SectionTitle>What applies to you — compliance scope</SectionTitle>
          <div className="mt-4 rounded-2xl border border-[color:var(--tcc-line)] overflow-hidden">
            {scope.map((s, i) => (
              <div key={s.name} className={`p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 ${i > 0 ? "border-t border-[color:var(--tcc-line)]" : ""}`}>
                <div className="sm:w-56 shrink-0">
                  <div className="font-sora font-bold text-[color:var(--tcc-navy)]">{s.name}</div>
                  <span className="inline-block mt-1 rounded-full px-2.5 py-0.5 font-mono-plex text-[10px] font-semibold uppercase tracking-wider text-white" style={{ background: s.color }}>{s.band}</span>
                </div>
                <div className="text-[13px] leading-relaxed text-[color:var(--tcc-ink-soft)] font-inter">{s.note}</div>
              </div>
            ))}
          </div>
        </section>

        {/* 6.4 Capability breakdown */}
        <section>
          <SectionTitle>Capability breakdown</SectionTitle>
          <div className="mt-3 rounded-2xl border border-[color:var(--tcc-line)] p-4 sm:p-5">
            {areaList.map((a) => <Bar key={a.key} area={a} />)}
            <div className="mt-3 font-mono-plex text-[10px] text-[color:var(--tcc-ink-soft)]">▲ Marker shows a typical business of similar size and sector.</div>
          </div>
        </section>

        {/* 6.5 How you compare */}
        <section className="tcc-no-break">
          <SectionTitle>How you compare</SectionTitle>
          <div className="mt-4 rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg,#1E77C4,#3FB8E1)" }}>
            <div className="flex flex-wrap items-end gap-6">
              <div><div className="font-mono-plex text-[11px] uppercase tracking-wider text-white/70">Your score</div><div className="font-sora text-4xl font-extrabold">{overall}</div></div>
              <div><div className="font-mono-plex text-[11px] uppercase tracking-wider text-white/70">Peer average</div><div className="font-sora text-4xl font-extrabold">{peerAvg}</div></div>
              <div className="flex-1 min-w-[200px]">
                <p className="font-inter text-[15px] leading-relaxed">
                  Your overall maturity is <strong>{verdict}</strong> a typical organisation of your size and sector.
                </p>
              </div>
            </div>
            <div className="mt-3 font-mono-plex text-[10px] text-white/70">Peer values are indicative estimates, not audited benchmark data.</div>
          </div>
        </section>

        {/* 6.6 Areas requiring verification */}
        {blindSpots.length > 0 && (
          <section className="tcc-no-break">
            <div className="rounded-2xl border-2 border-dashed border-[color:var(--tcc-warning)] bg-[#FDF6EC] p-5">
              <h3 className="font-sora text-lg font-bold text-[color:var(--tcc-ink)]">Points requiring verification</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-[color:var(--tcc-ink-soft)] font-inter">
                You indicated uncertainty on the following. In our experience, uncertainty is itself a finding — establishing the facts here is usually the first step we would take together.
              </p>
              <ul className="mt-3 space-y-1.5">
                {blindSpots.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[14px] text-[color:var(--tcc-ink)]">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--tcc-warning)" }} />
                    <span><strong>{b.control}</strong> <span className="font-mono-plex text-[11px] text-[color:var(--tcc-ink-soft)]">· {b.area}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* 6.7 Priority risk register */}
        <section>
          <SectionTitle>Priority risk register</SectionTitle>
          <p className="mt-2 text-[13px] text-[color:var(--tcc-ink-soft)] font-inter">Your responses translated into business risk, ranked by severity (control weight × capability gap).</p>
          <div className="mt-4 space-y-3">
            {risks.slice(0, 6).map((r) => (
              <div key={r.id} className="tcc-no-break rounded-xl border border-[color:var(--tcc-line)] p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="rounded-md px-2 py-0.5 font-mono-plex text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: r.color }}>{r.level}</span>
                  <span className="font-sora font-bold text-[color:var(--tcc-ink)]">{r.control}</span>
                  <span className="font-mono-plex text-[10px] uppercase tracking-wider text-[color:var(--tcc-ink-soft)]">{r.area}</span>
                </div>
                <p className="mt-2 text-[13px] leading-relaxed text-[color:var(--tcc-ink-soft)] font-inter">{r.risk}</p>
              </div>
            ))}
            {risks.length === 0 && <div className="text-[14px] text-[color:var(--tcc-ok)] font-semibold">No material risks identified — a strong result. We would still verify key controls together.</div>}
          </div>
        </section>

        {/* 6.8 Remediation roadmap */}
        <section>
          <SectionTitle>Remediation roadmap</SectionTitle>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            <RoadmapCard title="Immediate" window="0–90 days" accent="var(--tcc-danger)" items={roadmap.immediate} />
            <RoadmapCard title="Near term" window="3–6 months" accent="var(--tcc-warning)" items={roadmap.nearTerm} />
            <RoadmapCard title="Structural" window="6–12 months" accent="var(--tcc-blue)" items={roadmap.structural} />
          </div>
        </section>

        {/* 6.9 What we would verify together */}
        {verifyLines.length > 0 && (
          <section>
            <SectionTitle>What we would verify together</SectionTitle>
            <p className="mt-2 text-[13px] text-[color:var(--tcc-ink-soft)] font-inter">
              Every response here reflects your own view of the business — which is precisely the right starting point. The following are the areas a Techmate review would establish in practice.
            </p>
            <ul className="mt-4 space-y-2.5">
              {verifyLines.map((v, i) => (
                <li key={i} className="tcc-no-break flex items-start gap-2 text-[13px] leading-relaxed text-[color:var(--tcc-ink-soft)] font-inter">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--tcc-blue)" }} />
                  <span>You indicated: <em className="text-[color:var(--tcc-ink)] not-italic font-medium">{v.chosen}</em>. In a full review we would confirm this by examining <strong>{v.control}</strong>.</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 6.10 Actions */}
        <section className="print-hide flex flex-wrap gap-3 pt-2">
          <button onClick={() => window.print()} data-testid="report-print-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white" style={{ background: "linear-gradient(135deg,#EE6C1F,#FF9124)" }}>
            <Printer className="h-4 w-4" /> Print / save as PDF
          </button>
          <a href={mailto} data-testid="report-send-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white" style={{ background: "linear-gradient(135deg,#1E77C4,#3FB8E1)" }}>
            <Mail className="h-4 w-4" /> Send results to Techmate
          </a>
          <button onClick={onRestart} data-testid="report-restart-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-[color:var(--tcc-ink)] border border-[color:var(--tcc-line)] bg-white">
            <RotateCcw className="h-4 w-4" /> Restart assessment
          </button>
        </section>

        {/* 6.11 Footer */}
        <footer className="tcc-no-break border-t border-[color:var(--tcc-line)] pt-6 text-[12px] leading-relaxed text-[color:var(--tcc-ink-soft)] font-inter">
          <div className="font-semibold text-[color:var(--tcc-ink)]">{FIRM.name}</div>
          <div>{FIRM.address} · {FIRM.phone} · {FIRM.email}</div>
          <p className="mt-3">This report is an indicative self-assessment based on your own responses, structured against NIST CSF 2.0 and the Indian regulatory requirements applicable to your sector. It is a starting point for discussion, not a formal audit or assurance opinion. For a detailed review, our team is available.</p>
        </footer>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-sora text-xl sm:text-2xl font-bold text-[color:var(--tcc-ink)] flex items-center gap-2"><span className="h-5 w-1.5 rounded-full" style={{ background: "var(--tcc-orange)" }} />{children}</h2>;
}

function RoadmapCard({ title, window, accent, items }) {
  return (
    <div className="tcc-no-break rounded-2xl border border-[color:var(--tcc-line)] overflow-hidden">
      <div className="p-3 text-white" style={{ background: accent }}>
        <div className="font-sora font-bold">{title}</div>
        <div className="font-mono-plex text-[10px] uppercase tracking-wider text-white/85">{window}</div>
      </div>
      <div className="p-4 space-y-3">
        {items.length === 0 && <div className="text-[13px] text-[color:var(--tcc-ink-soft)] font-inter">Nothing pressing in this window.</div>}
        {items.map((r, i) => (
          <div key={i}>
            <div className="text-[13px] leading-snug text-[color:var(--tcc-ink)] font-inter">{r.action}</div>
            <div className="font-mono-plex text-[10px] text-[color:var(--tcc-blue)] mt-0.5">{r.control}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildNarrative(report, sector) {
  const { profile, tier, strongest, weakest, criticalCount, highCount } = report;
  const band = profile.A2;
  const a3i = ["Business records only", "Staff & supplier data", "Customer personal or payment data", "Regulated or highly sensitive data"].indexOf(profile.A3);
  const a4i = ["No", "Unsure", "Yes — minor", "Yes — significant"].indexOf(profile.A4);
  let s = `For a ${band} person ${sector.toLowerCase()} business, your overall cyber maturity sits in the ${tier.tier} range. `;
  s += `Your strongest area is ${strongest.name}; ${weakest.name} requires the most attention. `;
  const cc = criticalCount, hc = highCount;
  if (cc + hc > 0) {
    s += `We have identified ${cc} critical and ${hc} high-priority ${cc + hc === 1 ? "exposure" : "exposures"} that would benefit from early attention. `;
  } else {
    s += `Encouragingly, no critical or high-priority exposures were identified from your responses. `;
  }
  if (a4i >= 2) s += `Because you noted a security incident in the past two years, detection and recovery carry added priority. `;
  if (a3i >= 2) s += `As your business holds customer personal or payment data, strengthening protection and DPDP compliance is especially important. `;
  return s.trim();
}
